<template>
  <v-card class="tutor-header-card" data-test="tutor-header">
    <div class="tutor-header">
      <div class="avatar-container" :class="{ 'vip-border': info.isVip }" data-test="avatar">
        <v-avatar v-if="hasProfileImage" size="56" class="avatar-image">
          <v-img :src="info.profileImg" :alt="`Foto de ${info.name || 'Usuario'}`" />
        </v-avatar>
        <span v-else class="avatar-initials" data-test="initials">{{ initials }}</span>
      </div>

      <div class="tutor-identity">
        <div class="tutor-name-row">
          <span class="tutor-name" data-test="name">{{ info.name || '—' }}</span>
          <CopyButton
            :text="copyText"
            large
            label="Copiar datos del tutor"
            data-test="copy-tutor"
            @error="$emit('copy-error')"
          />
          <v-chip
            v-if="info.state"
            small
            outlined
            :color="info.state === 'Activo' ? 'green' : 'red'"
            class="font-weight-bold"
            data-test="chip-state"
          >{{ info.state }}</v-chip>
          <v-chip v-if="info.isVip" small outlined color="orange" class="font-weight-bold" data-test="chip-vip">
            VIP
          </v-chip>
          <v-chip
            v-if="info.identityValidatedAt"
            small
            outlined
            color="green"
            class="font-weight-bold"
            data-test="chip-identity"
          >
            <v-icon left x-small>mdi-shield-check-outline</v-icon>Identidad verificada
          </v-chip>
          <v-chip v-else small outlined color="grey" class="font-weight-bold" data-test="chip-identity">
            <v-icon left x-small>mdi-shield-outline</v-icon>Identidad pendiente
          </v-chip>
        </div>

        <!-- Contacto y último acceso en una sola línea, cada dato con su rótulo. -->
        <div class="tutor-contact pw-num" data-test="contact">
          <span><span class="contact-label">{{ identifierLabel }}</span>{{ info.rut || '—' }}</span>
          <span><span class="contact-label">Email</span>{{ info.email || '—' }}</span>
          <span><span class="contact-label">Tel.</span>{{ info.phone || '—' }}</span>
          <span><span class="contact-label">Últ. acceso</span>{{ info.lastAccess || '—' }}</span>
        </div>

        <div v-if="isBlacklisted || isSuspicious || overdueHealth" class="tutor-signals" data-test="signals">
          <v-chip v-if="isBlacklisted" small outlined color="red" class="font-weight-bold" data-test="chip-blacklist">
            <v-icon left x-small>mdi-cancel</v-icon>Lista negra
          </v-chip>
          <v-chip v-if="isSuspicious" small outlined color="orange" class="font-weight-bold" data-test="chip-suspicious">
            <v-icon left x-small>mdi-alert-outline</v-icon>Sospechoso
          </v-chip>
          <v-chip
            v-if="overdueHealth"
            small
            outlined
            color="error"
            class="font-weight-bold"
            title="Ver el detalle por mascota en «Coberturas y servicios»"
            data-test="chip-overdue-health"
          >
            <v-icon left x-small>mdi-needle</v-icon>{{ overdueHealth }} atrasada
          </v-chip>
        </div>

        <!-- Contratos: a la vista los que tienen membresías activas; los demás se resumen en
             «+N sin activas». Cualquier clic abre la gestión de contratos. -->
        <div v-if="contracts.length" class="tutor-contracts" data-test="contracts">
          <span class="contact-label">Contratos</span>
          <v-chip
            v-for="contract in activeContracts"
            :key="contract.id"
            small
            outlined
            color="primary"
            class="font-weight-bold"
            :title="contract.planName || ''"
            :data-test="`contract-${contract.id}`"
            @click="$emit('manage-contracts')"
          >
            {{ contract.companyName || '-' }} · {{ contract.contratoName || '-' }} ·
            {{ activeLabel(contract.activeMemberships) }}
          </v-chip>
          <a
            v-if="idleContracts.length"
            href="#"
            class="more-contracts"
            :title="idleContractsTitle"
            data-test="contracts-idle"
            @click.prevent="$emit('manage-contracts')"
          >{{ idleContractsText }}</a>
        </div>
      </div>

      <div class="tutor-actions">
        <v-btn
          v-if="can('users:update')"
          small
          depressed
          outlined
          color="primary"
          data-test="edit-user"
          @click="$emit('edit-user')"
        >
          <v-icon left small>mdi-pencil</v-icon>Editar tutor
        </v-btn>
        <v-btn
          v-if="can('users:add-pet')"
          small
          depressed
          outlined
          color="green darken-1"
          data-test="add-pet"
          @click="$emit('add-pet')"
        >
          <v-icon left small>mdi-plus</v-icon>Agregar mascota
        </v-btn>

        <v-menu v-if="menuGroups.length" offset-y left eager>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              depressed
              outlined
              color="secondary"
              aria-label="Más acciones"
              aria-haspopup="menu"
              data-test="more-actions"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small>mdi-dots-horizontal</v-icon>
              <span class="ml-1">Más acciones</span>
            </v-btn>
          </template>

          <v-list dense role="menu" class="more-actions-menu" data-test="more-actions-menu">
            <template v-for="(group, index) in menuGroups">
              <v-divider v-if="index > 0" :key="`divider-${group.title}`" />
              <v-subheader :key="`header-${group.title}`" class="menu-group-title" data-test="menu-group">
                {{ group.title }}
              </v-subheader>
              <v-list-item
                v-for="item in group.items"
                :key="item.event"
                role="menuitem"
                :class="{ 'menu-item--danger': item.danger }"
                :data-test="`menu-${item.event}`"
                @click="$emit(item.event)"
              >
                <v-list-item-icon class="mr-3">
                  <v-icon small :color="item.danger ? 'error' : undefined">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.label }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </div>
    </div>
  </v-card>
</template>

<script>
import CopyButton from './CopyButton.vue';
import { tutorCopyText } from '@/utils/vision360.utils';
import RutUtils from '@/utils/rut.utils';
import { can, canAct } from '@/utils/permissions';
import { overdueSummary } from '@/utils/pet-health.utils';

// Cabecera fija de la 360 (113 US1): quién es el tutor, sus señales de riesgo y sus acciones.
// Solo presenta datos y emite eventos; el contenedor (Vision360.vue) abre los diálogos.

// Acciones secundarias del menú «⋯», agrupadas como en el prototipo. Las de riesgo van en rojo.
const MENU_GROUPS = [
  {
    title: 'Cuenta',
    items: [
      {
        event: 'manage-contracts',
        label: 'Gestionar contratos',
        icon: 'mdi-domain',
        permission: 'users:manage-company-access'
      },
      {
        event: 'add-co-tutor',
        label: 'Agregar co-tutor',
        icon: 'mdi-account-multiple-plus-outline',
        permission: 'co-tutors:create'
      },
      {
        event: 'edit-note',
        label: 'Editar nota interna',
        icon: 'mdi-note-edit-outline',
        permission: ['users:update', 'users:view-security']
      }
    ]
  },
  {
    title: 'Acceso',
    items: [
      {
        event: 'reset-password',
        label: 'Reiniciar contraseña',
        icon: 'mdi-lock-reset',
        permission: 'users:reset-password'
      },
      {
        event: 'force-logout',
        label: 'Cerrar todas las sesiones',
        icon: 'mdi-logout',
        danger: true,
        permission: 'users:force-logout'
      }
    ]
  },
  {
    title: 'Riesgo',
    items: [
      {
        event: 'suspicious',
        label: 'Marcar como sospechoso',
        icon: 'mdi-alert-outline',
        danger: true,
        permission: ['users:flag-suspicious', 'users:view-security']
      },
      {
        event: 'blacklist',
        label: 'Lista negra',
        icon: 'mdi-cancel',
        danger: true,
        permission: ['users:blacklist', 'users:view-security']
      }
    ]
  }
];

// Only the actions the admin holds (every tag of `permission`); a group left
// without actions is dropped (spec 132).
function allowedMenuGroups() {
  return MENU_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => [].concat(item.permission).every((tag) => canAct(tag)))
  })).filter((group) => group.items.length);
}

// Una señal cuenta como vigente si existe y no viene marcada explícitamente como inactiva.
const isActiveSignal = (signal) => Boolean(signal) && signal.active !== false;

export default {
  name: 'Tutor360Header',
  components: { CopyButton },
  props: {
    // Forma de dataMixin.buildUserInfo; null mientras carga o si falló la carga del tutor.
    userInfo: { type: Object, default: null },
    // Señales que carga el contenedor: { blacklist: { reason } | null, suspicious: { reason } | null }.
    riskSignals: { type: Object, default: () => ({ blacklist: null, suspicious: null }) },
    // Asociaciones empresa/contrato del tutor (CompaniesService.getUserCompanyAccesses).
    contracts: { type: Array, default: () => [] },
    // Respuesta de GET /pets/360/health-status/:pgId; null mientras carga o si falló.
    petHealth: { type: Object, default: null },
    // URL absoluta de esta 360, para el texto de «Copiar datos».
    profileLink: { type: String, default: '' }
  },
  data() {
    return { menuGroups: allowedMenuGroups() };
  },
  computed: {
    info() {
      return this.userInfo || {};
    },
    // RUT para Chile, DNI para el resto de los países.
    identifierLabel() {
      return RutUtils.identifierLabel(this.info.country);
    },
    // Sin foto (o con la imagen por defecto) se muestran las iniciales, que distinguen mejor.
    hasProfileImage() {
      const img = this.info.profileImg;
      return Boolean(img) && !img.includes('default-user.png');
    },
    initials() {
      const parts = String(this.info.name || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '—';
      const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (parts[0][0] + last).toUpperCase();
    },
    isBlacklisted() {
      return isActiveSignal(this.riskSignals?.blacklist);
    },
    // Contratos con al menos una membresía activa: los que importan a la vista.
    activeContracts() {
      return this.contracts.filter((c) => Number(c.activeMemberships) > 0);
    },
    idleContracts() {
      return this.contracts.filter((c) => !this.activeContracts.includes(c));
    },
    // Sin contratos activos no hay chip delante, así que el texto no lleva «+».
    idleContractsText() {
      const n = this.idleContracts.length;
      const prefix = this.activeContracts.length ? '+' : '';
      return `${prefix}${n} sin activas`;
    },
    idleContractsTitle() {
      return this.idleContracts.map((c) => `${c.companyName || '-'} · ${c.contratoName || '-'}`).join('\n');
    },
    copyText() {
      return tutorCopyText(this.info, this.profileLink);
    },
    isSuspicious() {
      return isActiveSignal(this.riskSignals?.suspicious);
    },
    // 'Vacunas' / 'Desparasitación' / ambas si alguna mascota tiene algo atrasado; null si no.
    overdueHealth() {
      return overdueSummary(this.petHealth);
    }
  },
  methods: {
    can,
    canAct,
    activeLabel(count) {
      const n = Number(count) || 0;
      return n === 1 ? '1 activa' : `${n} activas`;
    }
  }
};
</script>

<style scoped>
.tutor-header-card {
  margin-bottom: 16px;
}
.tutor-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 20px;
}

.avatar-container {
  width: 56px;
  height: 56px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--pw-primary-tint);
  border-radius: 50%;
  overflow: hidden;
}
.avatar-initials {
  font-size: 1.15rem;
  font-weight: 600;
  line-height: 1;
  color: var(--pw-primary);
}
.avatar-image {
  border: 1px solid var(--pw-surface);
}
.vip-border {
  border: 3px solid var(--pw-warning) !important;
}

.tutor-identity {
  flex: 1 1 320px;
  min-width: 0;
}
.tutor-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.tutor-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--pw-text);
  margin-right: 4px;
  word-break: break-word;
}

.tutor-contact {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin-top: 4px;
  font-size: 0.8rem;
  color: var(--pw-text);
}
.contact-label {
  margin-right: 6px;
  font-weight: 600;
  color: var(--pw-text-muted);
}
.more-contracts {
  font-size: 0.8rem;
  color: var(--pw-text-muted);
  text-decoration: underline dotted;
}

.tutor-signals {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.tutor-contracts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.8rem;
}

.tutor-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.menu-group-title {
  height: 32px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-muted);
}
.menu-item--danger .v-list-item__title {
  color: var(--pw-error);
}
</style>
